import React, { useEffect, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import {
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider
} from 'react-router-dom'

import { TailwindContext } from './context/TailwindContext'
import { theme, getBreakpointInPixels } from './services/tailwind'
import { AppProvider } from './context/AppContext'
import httpClient from './http/httpClient'
import useStore from './services/useStore'
import useSessionStore from './services/useSessionStore'
import { reportError } from './utils/errorTracking'
import { lazyWithRetry } from './utils/lazyWithRetry'
import AppErrorFallback, { RouteErrorBoundary } from './components/AppErrorFallback'
import AppLoading from './components/AppLoading'

const In = lazyWithRetry(() => import('./pages/in/In'))
const Auth = lazyWithRetry(() => import('./pages/auth/Auth'))
const QrCode = lazyWithRetry(() => import('./pages/qr/QrCode'))
const Impersonate = lazyWithRetry(() => import('./Impersonate'))

class AppErrorBoundary extends React.Component<{ children: ReactNode }, { error: boolean }> {
  constructor (props: { children: ReactNode }) {
    super(props)
    this.state = { error: false }
  }

  static getDerivedStateFromError () {
    return { error: true }
  }

  componentDidCatch (error: Error, errorInfo: React.ErrorInfo) {
    reportError(error, errorInfo)
  }

  render () {
    if (this.state.error) {
      return <AppErrorFallback />
    }

    return this.props.children
  }
}

const queryClient = new QueryClient()

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RouteErrorBoundary />}>
      <Route path="/app/in/*" element={<React.Suspense fallback={<AppLoading />}><In /></React.Suspense>} />
      <Route path="/app/:locale/*" element={<React.Suspense fallback={<AppLoading />}><Auth /></React.Suspense>} />
      <Route path="/app/qr/:locale/*" element={<React.Suspense fallback={<AppLoading />}><QrCode /></React.Suspense>} />
      <Route path="/app/user/login" element={<React.Suspense fallback={<AppLoading />}><Impersonate /></React.Suspense>} />
      <Route path="*" element={<Navigate to="/app/en" />} />
    </Route>
  )
)

function GlobalInterceptorSetup () {
  const authToken = useStore(state => state.authToken)
  const partnerUserConfig = useSessionStore(state => state.partnerUserConfig)

  useEffect(() => {
    let authInterceptor = 0
    let partnerInterceptor = 0

    // Add X-Auth token interceptor when user is authenticated
    if (authToken) {
      authInterceptor = httpClient.interceptors.request.use((config) => {
        config.headers['X-Auth'] = `Bearer ${authToken}`
        return config
      })

      // Add partner header interceptor only when user is authenticated AND has partner config
      if (partnerUserConfig?.partner?.id) {
        partnerInterceptor = httpClient.interceptors.request.use((config) => {
          config.headers.partner = partnerUserConfig.partner.id
          return config
        })
      }
    }

    return () => {
      if (authInterceptor !== 0) {
        httpClient.interceptors.request.eject(authInterceptor)
      }
      if (partnerInterceptor !== 0) {
        httpClient.interceptors.request.eject(partnerInterceptor)
      }
    }
  }, [authToken, partnerUserConfig?.partner?.id])

  return null
}

export default function App () {
  return (
    <AppErrorBoundary>
      <TailwindContext.Provider value={{ theme, getBreakpointInPixels }}>
        <QueryClientProvider client={queryClient}>
          <AppProvider>
            <GlobalInterceptorSetup />
            <RouterProvider router={router} />
          </AppProvider>
        </QueryClientProvider>
      </TailwindContext.Provider>
    </AppErrorBoundary>
  )
}
